import { ref, computed } from 'vue';
import bondService from '@/service/bondService';

export function useCreditIssuers() {
  // State
  const issuersData = ref({});
  const bondTypes = ref([]); // 동적으로 로드될 타입들
  const expandedIssuers = ref([]);
  const isLoading = ref(false);
  const currentType = ref('');

  // 기본 크레딧 타입 (fallback) - 백엔드 CreditType enum과 일치
  const defaultBondTypes = [
    { type: 'PUBLIC', title: '공사채' },
    { type: 'BANK', title: '은행채' },
    { type: 'CREDIT_FINANCE', title: '여전채' },
    { type: 'COMPANY', title: '회사채' },
  ];

  // Computed
  const activeBondTypes = computed(() => {
    return bondTypes.value.length > 0 ? bondTypes.value : defaultBondTypes;
  });

  const typeNames = computed(() => {
    const names = {};
    activeBondTypes.value.forEach(bondType => {
      names[bondType.type] = bondType.title;
    });
    return names;
  });

  const sortedIssuers = computed(() => {
    return (
      issuersData.value[currentType.value]?.sort((a, b) => {
        return a.issuerName?.localeCompare(b.issuerName, 'ko-KR') || 0;
      }) || []
    );
  });

  // Methods
  const initializeDataStructure = () => {
    const newIssuersData = {};

    activeBondTypes.value.forEach(bondType => {
      newIssuersData[bondType.type] = issuersData.value[bondType.type] || [];
    });

    issuersData.value = newIssuersData;

    // currentType 초기화
    if (!currentType.value && activeBondTypes.value.length > 0) {
      currentType.value = activeBondTypes.value[0].type;
    }
  };

  const loadBondTypes = async () => {
    try {
      const response = await bondService.getCreditBondTypes();
      bondTypes.value = response || [];

      // 데이터 구조 초기화
      initializeDataStructure();
    } catch (error) {
      console.error('크레딧 타입 로드 실패:', error);
      // 에러 시 기본값 사용
      bondTypes.value = [];
      initializeDataStructure();
    }
  };

  const loadIssuersData = async () => {
    try {
      const response = await bondService.getCreditIssuers();

      // BondIssuerResponse 구조에 맞게 데이터 처리
      const issuerMap = {
        PUBLIC: response._public || [],
        BANK: response.bank || [],
        CREDIT_FINANCE: response.creditFinance || [],
        COMPANY: response.company || [],
      };

      // 타입별로 데이터 분류
      activeBondTypes.value.forEach(bondType => {
        // API 응답의 키와 bondType.type 매핑
        const mappedKey = mapCreditTypeToResponseKey(bondType.type);
        issuersData.value[bondType.type] = issuerMap[mappedKey] || [];
      });
    } catch (error) {
      console.error('발행사 데이터 로드 실패:', error);
      throw error;
    }
  };

  const mapCreditTypeToResponseKey = type => {
    const mapping = {
      PUBLIC: 'PUBLIC',
      BANK: 'BANK',
      CREDIT_FINANCE: 'CREDIT_FINANCE',
      COMPANY: 'COMPANY',
    };
    return mapping[type] || type;
  };

  const loadCreditData = async () => {
    try {
      isLoading.value = true;

      // 타입을 먼저 로드하고 데이터 구조 초기화
      await loadBondTypes();

      // 발행사 데이터 로드
      await loadIssuersData();
    } catch (error) {
      console.error('크레딧 데이터 로드 실패:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const createIssuer = async issuerData => {
    try {
      isLoading.value = true;
      await bondService.createIssuer(issuerData);
      
      // 발행사 생성 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();
      
      return { success: true };
    } catch (error) {
      console.error('발행사 생성 실패:', error);
      throw new Error('발행사 생성 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const updateIssuer = async (issuerId, issuerData) => {
    try {
      isLoading.value = true;
      await bondService.updateIssuer(
        issuerId,
        issuerData
      );

      // 발행사 수정 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();

      return { success: true };
    } catch (error) {
      console.error('발행사 수정 실패:', error);
      throw new Error('발행사 수정 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const createAlias = async (issuerId, aliasName) => {
    try {
      await bondService.createAlias(issuerId, aliasName);

      // 별칭 생성 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();

      return { success: true };
    } catch (error) {
      console.error('별칭 생성 실패:', error);
      throw new Error('별칭 생성 중 오류가 발생했습니다.');
    }
  };

  const deleteAlias = async (issuerId, aliasId) => {
    try {
      await bondService.deleteAlias(aliasId);

      // 별칭 삭제 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();
    } catch (error) {
      console.error('별칭 삭제 실패:', error);
      throw new Error('별칭 삭제 중 오류가 발생했습니다.');
    }
  };

  const toggleIssuerExpand = issuerId => {
    const index = expandedIssuers.value.indexOf(issuerId);
    if (index > -1) {
      expandedIssuers.value.splice(index, 1);
    } else {
      expandedIssuers.value.push(issuerId);
    }
  };

  const isIssuerExpanded = issuerId => {
    return expandedIssuers.value.includes(issuerId);
  };

  return {
    // State
    issuersData,
    bondTypes,
    expandedIssuers,
    isLoading,
    currentType,

    // Computed
    activeBondTypes,
    typeNames,
    sortedIssuers,

    // Methods
    loadCreditData,
    createIssuer,
    updateIssuer,
    createAlias,
    deleteAlias,
    toggleIssuerExpand,
    isIssuerExpanded,
  };
} 